import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User as UserIcon, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import { useAuth } from "@/hooks/useAuth";

import { cn } from '@/lib/utils';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from 'lucide-react';

const exploreLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const whatsappNumber = '919663306819';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi! I'm interested in learning about stock market trading.")}`;

  useEffect(() => {
    if (isMenuOpen) {
      document.body.setAttribute('data-menu-open', 'true');
    } else {
      document.body.removeAttribute('data-menu-open');
      setMobileExploreOpen(false);
    }
  }, [isMenuOpen]);

  const mainNavLinks = user
    ? [
      { href: '/courses', label: 'Courses' },
      { id: 'explore', label: 'Explore', isDropdown: true },
      { href: '/my-courses', label: 'My Courses' },
    ]
    : [
      { href: '/', label: 'Home' },
      { href: '/about', label: 'About' },
      { href: '/courses', label: 'Courses' },
      { href: '/blog', label: 'Blog' },
      { href: '/contact', label: 'Contact' },
    ];

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 border-b border-gray-100/60 shadow-sm transition-all duration-300 backdrop-blur-md bg-white/90",
      isMenuOpen ? "z-[100]" : "z-50"
    )}>
      <div className="container-max px-4">
        <nav className="flex items-center justify-between h-[80px] md:h-[100px]">
          {/* Section 1: Logo & Brand */}
          <div className="flex-shrink-0 flex items-center">
            <Logo
              imageClassName="w-[52px] h-[52px] md:w-[80px] md:h-[80px]"
              textClassName="flex flex-col -gap-0.5 ml-1"
              className="gap-4"
            />
          </div>

          {/* Section 2: Navigation Links (Desktop) */}
          <div className="hidden lg:flex items-center gap-10">
            {mainNavLinks.map((link) => {
              if (link.isDropdown) {
                const isAnyChildActive = exploreLinks.some(child => location.pathname === child.href);
                return (
                  <DropdownMenu key={link.id}>
                    <DropdownMenuTrigger asChild>
                      <button className={cn(
                        "group relative text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 whitespace-nowrap pt-1 flex items-center gap-1 outline-none",
                        isAnyChildActive ? "text-[#00008B]" : "text-primary/40 hover:text-primary"
                      )}>
                        {link.label}
                        <ChevronDown className={cn("w-3 h-3 transition-transform duration-300 group-data-[state=open]:rotate-180", isAnyChildActive ? "opacity-100" : "opacity-40")} />
                        <span
                          className={cn(
                            "absolute -bottom-1.5 left-0 h-[2px] bg-accent transition-all duration-300 rounded-full",
                            isAnyChildActive ? "w-full" : "w-0 group-hover:w-full"
                          )}
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="min-w-[180px] p-2 bg-white/95 backdrop-blur-xl border-gray-100 shadow-premium animate-stack-in rounded-[1.5rem]"
                    >
                      {exploreLinks.map((child) => (
                        <DropdownMenuItem key={child.href} asChild>
                          <Link
                            to={child.href}
                            className={cn(
                              "w-full px-4 py-3 text-[9px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer",
                              location.pathname === child.href
                                ? "bg-accent/10 text-[#00008B]"
                                : "text-primary/60 hover:bg-primary/5 hover:text-primary"
                            )}
                          >
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              const isActive = location.pathname === link.href;

              return (
                <Link
                  key={link.href}
                  to={link.href!}
                  className={cn(
                    "group relative text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 whitespace-nowrap pt-1",
                    isActive ? "text-[#00008B]" : "text-primary/40 hover:text-primary"
                  )}
                >
                  {link.label}
                  <span
                    className={cn(
                      "absolute -bottom-1.5 left-0 h-[2px] bg-accent transition-all duration-300 rounded-full",
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    )}
                  />
                </Link>
              );
            })}
          </div>

          {/* Section 3: Auth & CTA (Desktop) */}
          <div className="hidden lg:flex items-center gap-5">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2.5 px-4 py-2 bg-accent/5 rounded-full border border-accent/20 shadow-sm transition-all hover:bg-accent/10">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                    <UserIcon className="w-3 h-3 text-accent" />
                  </div>
                  <span className="text-[9px] font-black text-primary uppercase tracking-widest">{user.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="h-8 px-3 text-primary/40 hover:text-destructive hover:bg-destructive/5 text-[9px] font-black uppercase tracking-widest transition-all"
                >
                  <LogOut className="w-3 h-3 mr-1.5" />
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-10">
                {/* PORTAL LOGIN Plain Link */}
                <Link
                  to="/login"
                  className={cn(
                    "group relative text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 whitespace-nowrap pt-1",
                    location.pathname === '/login' ? "text-[#00008B]" : "text-primary/40 hover:text-primary"
                  )}
                >
                  PORTAL LOGIN
                  <span
                    className={cn(
                      "absolute -bottom-1.5 left-0 h-[2px] bg-accent transition-all duration-300 rounded-full",
                      location.pathname === '/login' ? "w-full" : "w-0 group-hover:w-full"
                    )}
                  />
                </Link>

                <Link to="/courses">
                  <Button className="btn-gold h-11 px-8 text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-premium hover:shadow-gold-strong transition-all duration-500">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2.5 hover:bg-primary/5 rounded-2xl transition-all border border-transparent hover:border-primary/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-primary" />
            ) : (
              <Menu className="w-6 h-6 text-primary" />
            )}
          </button>
        </nav>

        {/* Mobile Menu (Slide down) */}
        {isMenuOpen && (
          <div className="lg:hidden py-8 border-t border-gray-100 animate-fade-in bg-white/50 backdrop-blur-xl rounded-b-[2.5rem] max-h-[calc(100vh-80px)] overflow-y-auto pb-6">
            <div className="flex flex-col gap-1 px-2">
              {mainNavLinks.map((link) => {
                if (link.isDropdown) {
                  const isAnyChildActive = exploreLinks.some(child => location.pathname === child.href);
                  return (
                    <div key={link.id} className="flex flex-col">
                      <button
                        onClick={() => setMobileExploreOpen(!mobileExploreOpen)}
                        className={cn(
                          "px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-between",
                          (isAnyChildActive || mobileExploreOpen)
                            ? "bg-accent/5 text-accent"
                            : "text-primary/40 hover:bg-primary/5 hover:text-primary"
                        )}
                      >
                        {link.label}
                        <ChevronDown className={cn(
                          "w-4 h-4 transition-transform duration-300",
                          mobileExploreOpen ? "rotate-180" : ""
                        )} />
                      </button>

                      {mobileExploreOpen && (
                        <div className="flex flex-col gap-1 mt-1 ml-4 border-l-2 border-accent/10 pl-2 animate-in fade-in slide-in-from-top-2 duration-300">
                          {exploreLinks.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => {
                                setIsMenuOpen(false);
                                setMobileExploreOpen(false);
                              }}
                              className={cn(
                                "px-6 py-3 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all",
                                location.pathname === child.href
                                  ? "text-accent bg-accent/5"
                                  : "text-primary/60 hover:text-primary hover:bg-primary/5"
                              )}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.href}
                    to={link.href!}
                    onClick={() => {
                      setIsMenuOpen(false);
                      setMobileExploreOpen(false);
                    }}
                    className={cn(
                      "px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all",
                      location.pathname === link.href
                        ? "bg-accent/10 text-accent shadow-sm"
                        : "text-primary/40 hover:bg-primary/5 hover:text-primary"
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}

              {/* Mobile PORTAL LOGIN Plain Link */}
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    "px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all mx-2",
                    location.pathname === '/login'
                      ? "bg-accent/10 text-accent shadow-sm"
                      : "text-primary/40 hover:bg-primary/5 hover:text-primary"
                  )}
                >
                  PORTAL LOGIN
                </Link>
              )}

              <div className="mt-6 px-6 pt-6 border-t border-gray-100 space-y-6 pb-4">
                {/* 1. Get Started Button */}
                {!user && (
                  <div className="flex flex-col gap-4">
                    <Link to="/courses" onClick={() => setIsMenuOpen(false)}>
                      <Button className="btn-gold w-full h-16 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-premium">
                        Get Started
                      </Button>
                    </Link>
                  </div>
                )}

                {/* 2. WhatsApp Button (Inside Drawer) */}
                <div className="flex justify-center">
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <MessageCircle className="w-5 h-5" fill="white" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Chat with Us</span>
                  </a>
                </div>

                {/* 3 & 4. Authenticated Info & Sign Out (Compact Block) */}
                <div className="pt-4 border-t border-gray-50 flex flex-col items-center gap-2">
                  {user ? (
                    <>
                      <div className="flex flex-col items-center">
                        <span className="text-[8px] font-black uppercase tracking-widest text-primary/20">Authenticated As</span>
                        <span className="text-[11px] font-bold text-primary">{user.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { logout(); setIsMenuOpen(false); }}
                        className="text-destructive font-black text-[10px] uppercase tracking-widest hover:bg-destructive/5 gap-2 h-auto py-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    null
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
